/**
 * Canonical extraction taxonomy for server-side requirement extraction (Edge → Cloud Run).
 * Single source of truth — not gated on client Settings API key.
 *
 * @module extractionTaxonomy
 */

/**
 * Primary requirement categories (legacy `type` / `extraction_type` compatibility).
 * @type {Array<{ value: string, label: string }>}
 */
export const CANONICAL_EXTRACTION_TYPES = [
  { value: 'User Story', label: 'User Story' },
  { value: 'Business Requirement', label: 'Business Requirement' },
  { value: 'Functional Requirement', label: 'Functional Requirement' },
  { value: 'Technical Requirement', label: 'Technical Requirement' },
  { value: 'System Requirement', label: 'System Requirement' },
  { value: 'API Requirement', label: 'API Requirement' },
  { value: 'Security Requirement', label: 'Security Requirement' },
  { value: 'Performance Requirement', label: 'Performance Requirement' },
];

/**
 * Returns the same list as legacy `aiService.getExtractionTypes()` for UI pickers.
 * @returns {Array<{ value: string, label: string }>}
 */
export function getExtractionTypeOptions() {
  return [...CANONICAL_EXTRACTION_TYPES];
}

/**
 * Artifact format: how extracted items should be written (single-pass extraction).
 * @type {Array<{ id: string, label: string, help: string }>}
 */
export const ARTIFACT_FORMATS = [
  {
    id: 'Epic',
    label: 'Epic',
    help: 'Large outcomes grouped into themes; high-level scope statements.',
  },
  {
    id: 'User Story',
    label: 'User Story',
    help: 'Written as user-centric stories (e.g. “As a… I want… so that…”) where appropriate.',
  },
  {
    id: 'Tasks',
    label: 'Tasks',
    help: 'Actionable work items and concrete deliverables.',
  },
  {
    id: 'Themes',
    label: 'Themes',
    help: 'Cross-cutting themes or initiative buckets rather than single features.',
  },
  {
    id: 'Other',
    label: 'Other',
    help: 'Describe your own format below.',
  },
];

/**
 * Non-overlapping requirement dimensions (BABOK-style). AI may assign multiple per requirement.
 * User can exclude dimensions they do not want considered.
 * @type {Array<{ id: string, label: string, help: string }>}
 */
export const REQUIREMENT_DIMENSIONS = [
  { id: 'Functional', label: 'Functional', help: 'Behavior and capabilities the solution must provide.' },
  { id: 'Technical', label: 'Technical', help: 'Architecture, platforms, integration, and implementation constraints.' },
  { id: 'Security', label: 'Security', help: 'Confidentiality, integrity, availability, and compliance controls.' },
  { id: 'Stakeholder', label: 'Stakeholder (User)', help: 'Needs and expectations of users, sponsors, and affected parties.' },
  { id: 'Transition', label: 'Transition', help: 'Deployment, migration, training, cutover, and operational handoff.' },
  { id: 'Performance', label: 'Performance', help: 'Throughput, latency, scalability, and capacity.' },
  { id: 'Compliance', label: 'Compliance', help: 'Regulatory, policy, and contractual obligations.' },
];

/**
 * Default extraction configuration for new sessions.
 * @returns {{ artifactFormat: string, artifactFormatOther: string, excludedDimensions: string[] }}
 */
export function getDefaultExtractionConfig() {
  return {
    artifactFormat: 'User Story',
    artifactFormatOther: '',
    excludedDimensions: [],
  };
}

/**
 * All dimension IDs (none excluded).
 * @returns {string[]}
 */
export function getAllDimensionIds() {
  return REQUIREMENT_DIMENSIONS.map((d) => d.id);
}

/**
 * Build legacy `extractionType` array for DB/API backward compatibility.
 * @param {object} config - Extraction config.
 * @param {string} config.artifactFormat - Epic | User Story | Tasks | Themes | Other.
 * @param {string} [config.artifactFormatOther] - When format is Other.
 * @returns {string[]}
 */
export function extractionConfigToLegacyExtractionTypeArray(config) {
  if (!config || !config.artifactFormat) {
    return ['User Story'];
  }
  if (config.artifactFormat === 'Other' && config.artifactFormatOther?.trim()) {
    return [config.artifactFormatOther.trim()];
  }
  return [config.artifactFormat];
}

/**
 * Whether the config is complete enough to run extraction.
 * @param {object|null} config
 * @returns {boolean}
 */
export function isExtractionConfigValid(config) {
  if (!config?.artifactFormat) return false;
  if (config.artifactFormat === 'Other') {
    return Boolean(config.artifactFormatOther?.trim());
  }
  return true;
}

/**
 * Normalize session row fields into extraction config for UI + worker.
 * @param {object|null} extractionConfig - `sessions.extraction_config` JSON.
 * @param {string|string[]|null} extractionType - Legacy `extraction_type`.
 * @returns {{ artifactFormat: string, artifactFormatOther: string, excludedDimensions: string[] }}
 */
export function normalizeExtractionConfig(extractionConfig, extractionType) {
  if (extractionConfig && typeof extractionConfig === 'object' && extractionConfig.artifactFormat) {
    return {
      artifactFormat: extractionConfig.artifactFormat,
      artifactFormatOther: extractionConfig.artifactFormatOther || '',
      excludedDimensions: Array.isArray(extractionConfig.excludedDimensions)
        ? extractionConfig.excludedDimensions
        : [],
    };
  }
  let primary = 'User Story';
  if (typeof extractionType === 'string' && extractionType.trim()) {
    if (extractionType.startsWith('[')) {
      try {
        const parsed = JSON.parse(extractionType);
        if (Array.isArray(parsed) && parsed[0]) primary = parsed[0];
      } catch {
        primary = extractionType.split(',')[0]?.trim() || primary;
      }
    } else {
      primary = extractionType.split(',')[0]?.trim() || primary;
    }
  } else if (Array.isArray(extractionType) && extractionType[0]) {
    primary = extractionType[0];
  }
  const known = ARTIFACT_FORMATS.some((a) => a.id === primary);
  if (known) {
    return { artifactFormat: primary, artifactFormatOther: '', excludedDimensions: [] };
  }
  return {
    artifactFormat: 'Other',
    artifactFormatOther: primary,
    excludedDimensions: [],
  };
}
